import { useState, type FormEvent } from 'react';
import { FiEdit2, FiCheck, FiX, FiUser, FiBell, FiLock, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { BotaoAtivarPush } from '../../components/BotaoAtivarPush';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useCamposCadastro, inputTypeDoCampo } from '../../hooks/useCamposCadastro';
import { formatarCPF } from '@ponti/shared/utils';

export function PerfilPage() {
  const { cidadao, recarregarCidadao, signOut } = useAuth();
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);

  const [nome, setNome] = useState(cidadao?.nome ?? '');
  const [nomeSocial, setNomeSocial] = useState(cidadao?.nome_social ?? '');
  const [telefone, setTelefone] = useState(cidadao?.telefone ?? '');
  const [whatsapp, setWhatsapp] = useState(cidadao?.whatsapp ?? '');

  const camposCustom = useCamposCadastro();
  const [valoresCustom, setValoresCustom] = useState<Record<string, string>>(
    (cidadao?.campos_customizados as Record<string, string> | undefined) ?? {},
  );

  const [novaSenha, setNovaSenha] = useState('');
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  if (!cidadao) {
    return (
      <PortalPage titulo="Meu perfil" icone={<FiUser size={26} />} largura="media" voltarHref="/">
        <div className="text-center text-sm text-slate-500 py-10">
          Carregando perfil...
        </div>
      </PortalPage>
    );
  }

  function cancelar() {
    setNome(cidadao!.nome);
    setNomeSocial(cidadao!.nome_social ?? '');
    setTelefone(cidadao!.telefone ?? '');
    setWhatsapp(cidadao!.whatsapp ?? '');
    setValoresCustom((cidadao!.campos_customizados as Record<string, string> | undefined) ?? {});
    setEditando(false);
    setMensagem(null);
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const campoFaltando = camposCustom.find((c) => c.obrigatorio && !(valoresCustom[c.id]?.trim()));
    if (campoFaltando) {
      setMensagem({ tipo: 'erro', texto: `Preencha o campo "${campoFaltando.nome}".` });
      return;
    }
    setSalvando(true);
    setMensagem(null);
    const { error } = await supabase
      .from('cidadaos')
      .update({
        nome: nome.trim(),
        nome_social: nomeSocial.trim() || null,
        telefone: telefone.replace(/\D/g, '') || null,
        whatsapp: whatsapp.replace(/\D/g, '') || null,
        campos_customizados: valoresCustom,
      })
      .eq('id', cidadao!.id);
    setSalvando(false);
    if (error) {
      setMensagem({ tipo: 'erro', texto: error.message });
      return;
    }
    await recarregarCidadao();
    setEditando(false);
    setMensagem({ tipo: 'sucesso', texto: 'Perfil atualizado.' });
  }

  async function trocarSenha(e: FormEvent) {
    e.preventDefault();
    if (novaSenha.length < 8) {
      setMensagem({ tipo: 'erro', texto: 'A senha precisa ter no mínimo 8 caracteres.' });
      return;
    }
    setTrocandoSenha(true);
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    setTrocandoSenha(false);
    if (error) {
      setMensagem({ tipo: 'erro', texto: error.message });
      return;
    }
    setNovaSenha('');
    setMensagem({ tipo: 'sucesso', texto: 'Senha alterada com sucesso.' });
  }

  return (
    <PortalPage
      titulo="Meu perfil"
      subtitulo="Atualize seus dados de contato e altere sua senha."
      icone={<FiUser size={26} />}
      largura="media"
      voltarHref="/"
    >
      <div className="space-y-5">
        {mensagem && (
          <Alert variante={mensagem.tipo}>{mensagem.texto}</Alert>
        )}

        <SectionCard
          titulo="Dados pessoais"
          icone={<FiUser size={18} />}
          acoes={
            !editando ? (
              <Button variant="outline" onClick={() => setEditando(true)}>
                <FiEdit2 size={14} /> Editar
              </Button>
            ) : (
              <Button variant="outline" onClick={cancelar}>
                <FiX size={14} /> Cancelar
              </Button>
            )
          }
        >
          {!editando ? (
            <div className="space-y-3">
              <CampoLeitura label="Nome" valor={cidadao.nome} />
              {cidadao.nome_social && <CampoLeitura label="Nome social" valor={cidadao.nome_social} />}
              <CampoLeitura label="E-mail" valor={cidadao.email} />
              <CampoLeitura label="CPF" valor={cidadao.cpf ? formatarCPF(cidadao.cpf) : '—'} />
              <CampoLeitura label="Telefone" valor={cidadao.telefone ?? '—'} />
              <CampoLeitura label="WhatsApp" valor={cidadao.whatsapp ?? '—'} />
              {(cidadao.cidade || cidadao.uf) && (
                <CampoLeitura
                  label="Cidade"
                  valor={[cidadao.cidade, cidadao.uf].filter(Boolean).join(' / ')}
                />
              )}
              {camposCustom.map((campo) => (
                <CampoLeitura key={campo.id} label={campo.nome} valor={valoresCustom[campo.id]?.trim() || '—'} />
              ))}
            </div>
          ) : (
            <form onSubmit={salvar} className="space-y-4">
              <div>
                <Label htmlFor="nome" obrigatorio>Nome completo</Label>
                <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="nome_social">Nome social</Label>
                <Input id="nome_social" value={nomeSocial} onChange={(e) => setNomeSocial(e.target.value)} placeholder="Como você prefere ser chamado(a)" />
                <p className="text-xs text-slate-500 mt-1">Decreto 8.727/2016 — usado preferencialmente no atendimento.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tel">Telefone</Label>
                  <Input id="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(00) 0000-0000" />
                </div>
                <div>
                  <Label htmlFor="wa">WhatsApp</Label>
                  <Input id="wa" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="(00) 00000-0000" />
                </div>
              </div>
              {camposCustom.map((campo) => (
                <div key={campo.id}>
                  <Label htmlFor={`custom-${campo.id}`} obrigatorio={campo.obrigatorio}>{campo.nome}</Label>
                  <Input
                    id={`custom-${campo.id}`}
                    type={inputTypeDoCampo(campo.tipo)}
                    inputMode={campo.tipo === 'numero' ? 'numeric' : undefined}
                    value={valoresCustom[campo.id] ?? ''}
                    onChange={(e) => setValoresCustom((p) => ({ ...p, [campo.id]: e.target.value }))}
                    required={campo.obrigatorio}
                  />
                </div>
              ))}
              <Button type="submit" loading={salvando}>
                <FiCheck size={14} /> Salvar alterações
              </Button>
            </form>
          )}
        </SectionCard>

        <SectionCard
          titulo="Notificações no dispositivo"
          subtitulo="Receba avisos sobre seus protocolos diretamente no celular ou desktop, mesmo com o app fechado."
          icone={<FiBell size={18} />}
        >
          <BotaoAtivarPush />
        </SectionCard>

        <SectionCard titulo="Alterar senha" icone={<FiLock size={18} />}>
          <form onSubmit={trocarSenha} className="space-y-4">
            <div>
              <Label htmlFor="ns" obrigatorio>Nova senha</Label>
              <Input
                id="ns"
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <Button type="submit" loading={trocandoSenha}>Alterar senha</Button>
          </form>
        </SectionCard>

        <SectionCard titulo="Sair da conta" icone={<FiLogOut size={18} />}>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-500">Desconectar deste dispositivo.</p>
            <Button variant="outline" onClick={() => signOut()}>Sair</Button>
          </div>
        </SectionCard>
      </div>
    </PortalPage>
  );
}

function CampoLeitura({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-slate-900">{valor}</div>
    </div>
  );
}
